import React, { useMemo, useState } from 'react';

import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'components/buttons/Button';
import { Dropdown } from 'components/inputs/Dropdown';
import { TextField } from 'components/inputs/TextField.react';

import { usePhoneSetup } from '../../hooks/usePhoneSetup';
import { ErrorMessage } from '../common/ErrorMessage';

interface PhoneSetupStepProps {
    onComplete: () => void;
    onBack: () => void;
}

export const PhoneSetupStep = ({ onComplete, onBack }: PhoneSetupStepProps) => {
    const {
        step,
        phoneCountry,
        phoneNumber,
        isRegistering,
        isVerifying,
        registerError,
        verifyError,
        setPhoneCountry,
        setPhoneNumber,
        submitPhoneNumber,
        submitVerificationCode,
        changeNumber,
        finish,
    } = usePhoneSetup(onComplete);

    if (step === 'enter-number') {
        return (
            <EnterNumberStep
                countryCode={phoneCountry}
                phoneNumber={phoneNumber}
                isLoading={isRegistering}
                error={registerError}
                onCountryChange={setPhoneCountry}
                onPhoneChange={setPhoneNumber}
                onSubmit={submitPhoneNumber}
                onBack={onBack}
            />
        );
    }

    if (step === 'verify-code') {
        return (
            <VerifyPhoneStep
                phoneNumber={phoneNumber}
                countryCode={phoneCountry}
                isLoading={isVerifying}
                error={verifyError}
                onSubmit={submitVerificationCode}
                onChangeNumber={changeNumber}
            />
        );
    }

    // Success step
    return (
        <div>
            <h2 className="tw-text-l tw-font-semibold tw-mb-8">
                <FormattedMessage
                    id="login.phoneSetup.success.title"
                    defaultMessage="Phone number added"
                />
            </h2>
            <p className="tw-text-neutral-subdued tw-text-s tw-mb-16">
                <FormattedMessage
                    id="login.phoneSetup.success.description"
                    defaultMessage="Your phone number has been verified and added as a two-factor method."
                />
            </p>
            <Button variant="primary" wrapperClasses="tw-w-full" onClick={finish}>
                <FormattedMessage
                    id="login.phoneSetup.success.continue"
                    defaultMessage="Continue"
                />
            </Button>
        </div>
    );
};

// -------------------------------------------------------------------
// Enter phone number sub-step
// -------------------------------------------------------------------

interface EnterNumberStepProps {
    countryCode: string;
    phoneNumber: string;
    isLoading: boolean;
    error?: string;
    onCountryChange: (code: string) => void;
    onPhoneChange: (phone: string) => void;
    onSubmit: () => void;
    onBack: () => void;
}

const EnterNumberStep = ({
    countryCode,
    phoneNumber,
    isLoading,
    error,
    onCountryChange,
    onPhoneChange,
    onSubmit,
    onBack,
}: EnterNumberStepProps) => {
    const intl = useIntl();

    const countryItems = useMemo(
        () =>
            getCountries().map((country: CountryCode) => ({
                id: country,
                name: `${country} (+${getCountryCallingCode(country)})`,
            })),
        [],
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (phoneNumber.trim()) {
            onSubmit();
        }
    };

    return (
        <div>
            <h1 className="tw-text-xl tw-font-semibold tw-mb-8">
                <FormattedMessage id="login.phoneSetup.title" defaultMessage="Add phone number" />
            </h1>
            <p className="tw-text-neutral-subdued tw-text-s tw-mb-24">
                <FormattedMessage
                    id="login.phoneSetup.description"
                    defaultMessage="Add your phone number for two-factor authentication. We'll send a verification code to confirm."
                />
            </p>

            <form onSubmit={handleSubmit}>
                <div className="tw-flex tw-gap-8 tw-items-end tw-mb-12 tw-relative">
                    <div className="tw-w-[140px] tw-shrink-0">
                        <Dropdown
                            name="countryCode"
                            label={intl.formatMessage({
                                id: 'login.phoneSetup.country.label',
                                defaultMessage: 'Country',
                            })}
                            items={countryItems}
                            selectedId={countryCode as CountryCode}
                            onChange={(id: CountryCode) => onCountryChange(id)}
                            search
                            size="medium"
                        />
                    </div>
                    <div className="tw-flex-1">
                        <TextField
                            name="phoneNumber"
                            label={intl.formatMessage({
                                id: 'login.phoneSetup.phone.label',
                                defaultMessage: 'Phone number',
                            })}
                            placeholderText={intl.formatMessage({
                                id: 'login.phoneSetup.phone.placeholder',
                                defaultMessage: 'Enter your phone number',
                            })}
                            value={phoneNumber}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                onPhoneChange(e.target.value)
                            }
                            type="tel"
                            autoFocus
                            className=""
                        />
                    </div>
                </div>

                <ErrorMessage message={error} />

                <div className="tw-mt-16">
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isLoading}
                        wrapperClasses="tw-w-full"
                    >
                        <FormattedMessage
                            id="login.phoneSetup.sendVerification"
                            defaultMessage="Send verification code"
                        />
                    </Button>
                </div>
            </form>

            <button
                type="button"
                onClick={onBack}
                className="tw-text-primary tw-text-s tw-mt-16 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 hover:tw-underline"
            >
                <FormattedMessage
                    id="login.phoneSetup.back"
                    defaultMessage="Back to verification methods"
                />
            </button>
        </div>
    );
};

// -------------------------------------------------------------------
// Verify phone code sub-step
// -------------------------------------------------------------------

interface VerifyPhoneStepProps {
    phoneNumber: string;
    countryCode: string;
    isLoading: boolean;
    error?: string;
    onSubmit: (code: string) => void;
    onChangeNumber: () => void;
}

const VerifyPhoneStep = ({
    phoneNumber,
    countryCode,
    isLoading,
    error,
    onSubmit,
    onChangeNumber,
}: VerifyPhoneStepProps) => {
    const intl = useIntl();
    const [code, setCode] = useState('');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (code.trim()) {
            onSubmit(code.trim());
        }
    };

    const callingCode = countryCode ? getCountryCallingCode(countryCode as CountryCode) : undefined;
    const displayPhone = callingCode ? `+${callingCode} ${phoneNumber}` : phoneNumber;

    return (
        <div>
            <h1 className="tw-text-xl tw-font-semibold tw-mb-8">
                <FormattedMessage
                    id="login.phoneSetup.verify.title"
                    defaultMessage="Verify phone number"
                />
            </h1>
            <p className="tw-text-neutral-subdued tw-text-s tw-mb-4">
                <FormattedMessage
                    id="login.phoneSetup.verify.description"
                    defaultMessage="We sent a verification code to {phone}."
                    values={{ phone: <strong>{displayPhone}</strong> }}
                />
            </p>
            <button
                type="button"
                onClick={onChangeNumber}
                className="tw-text-primary tw-text-s tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 hover:tw-underline tw-mb-16"
            >
                <FormattedMessage
                    id="login.phoneSetup.verify.changeNumber"
                    defaultMessage="Change number"
                />
            </button>

            <form onSubmit={handleSubmit}>
                <TextField
                    name="verificationCode"
                    label={intl.formatMessage({
                        id: 'login.phoneSetup.verify.code.label',
                        defaultMessage: 'Verification code',
                    })}
                    placeholderText={intl.formatMessage({
                        id: 'login.phoneSetup.verify.code.placeholder',
                        defaultMessage: '6-digit code',
                    })}
                    value={code}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
                    autoFocus
                    autoComplete="one-time-code"
                    className=""
                />

                <ErrorMessage message={error} />

                <div className="tw-mt-16">
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isLoading}
                        wrapperClasses="tw-w-full"
                    >
                        <FormattedMessage
                            id="login.phoneSetup.verify.submit"
                            defaultMessage="Verify"
                        />
                    </Button>
                </div>
            </form>
        </div>
    );
};
